import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EMPTY_OBJECT } from '../../../../constants/common'
import { useNotification, useTimeZone, useUserData } from '../../../../hooks'
import { normalizeHtml } from '../../../../utils/html'
import { useDraftCreationOpener } from '../../../Draft'
import { createEmailMessage } from '../../Email.api'
import { EmailDraftCreateActionSelectModal } from '../../EmailDraftCreateActionSelectModal'
import { useEmailDraft } from '../useEmailDraft'
import { useEmailProfileContext } from '../useEmailProfileContext'
import { useEmailProfileMessagesListExpandContext } from '../useEmailProfileMessagesListExpandContext'
import { useEmailReplyFormInitEditor } from '../useEmailReplyFormInitEditor'
import { useEmailReplyFormShowConversation } from '../useEmailReplyFormShowConversation'

import { EMAIL_REPLY_FORM_MODE } from './useEmailReply.constants'
import {
  getEmailReplyDraftInitialValues,
  getReplyInitialValuesByMode,
} from './useEmailReply.utils'

export function useEmailReply({
  createMessageApi = createEmailMessage,
  data,
  emailInitial,
  onCloseModal,
  onSetModal,
  onSuccess,
}) {
  const { t } = useTranslation()

  const timeZone = useTimeZone()

  const currentUser = useUserData()

  const { handleAddSuccessNotification } = useNotification()

  const { setPerformingAction } = useEmailProfileContext()

  const { collapseMessage, expandMessage } =
    useEmailProfileMessagesListExpandContext()

  const {
    draft: emailDraft,
    draftId,
    setDraft: setEmailDraft,
  } = useEmailDraft()

  const [replyFormInitialValues, setReplyFormInitialValues] = useState(null)

  const [replyFormEnabled, setReplyFormEnabled] = useState(false)

  const [replyCreationAction, setReplyCreationAction] = useState(null)

  const { editorRef, handleInitEditor } = useEmailReplyFormInitEditor()

  const { toggleExpandConversation } = useEmailReplyFormShowConversation({
    editorRef,
  })

  function handleCancelReply() {
    setReplyFormEnabled(false)

    setReplyFormInitialValues(null)

    setReplyCreationAction(null)
  }

  function getReplyFormInitialValues({
    initialValues = emailInitial,
    mode,
  } = EMPTY_OBJECT) {
    const getInitialValues = getReplyInitialValuesByMode(mode)

    return {
      ...initialValues,
      ...getInitialValues({
        currentUserId: currentUser.id,
        data,
        t,
        timeZone,
      }),
    }
  }

  function handleActivateReplyForm(initialValues) {
    expandMessage(data.uuid)

    setReplyFormEnabled(true)

    setReplyFormInitialValues(initialValues)
  }

  function handleClickRemoveDraft() {
    setEmailDraft(null, 0)

    handleCancelReply()
  }

  function getInitialValues(payload = EMPTY_OBJECT) {
    return getReplyFormInitialValues({ mode: payload.mode })
  }

  function openReplyForm({ action, initialValues, mode } = EMPTY_OBJECT) {
    setReplyCreationAction(action ?? null)

    handleActivateReplyForm(
      initialValues
        ? getEmailReplyDraftInitialValues({ data, draft: initialValues })
        : getReplyFormInitialValues({ mode })
    )
  }

  function handleCreateNew() {
    setEmailDraft(null, 0)
  }

  const handleOpenReply = useDraftCreationOpener({
    DraftPickerModal: EmailDraftCreateActionSelectModal,
    closeModal: onCloseModal,
    draft: emailDraft,
    draftId,
    getInitialValues,
    openModal: openReplyForm,
    setModal: onSetModal,
    onCreateNew: handleCreateNew,
  })

  function handleClickReply() {
    handleOpenReply({ mode: EMAIL_REPLY_FORM_MODE.reply })
  }

  function handleClickReplyAll() {
    handleOpenReply({ mode: EMAIL_REPLY_FORM_MODE.replyAll })
  }

  function handleClickForward() {
    handleOpenReply({ mode: EMAIL_REPLY_FORM_MODE.forward })
  }

  function handleSubmit(data) {
    setPerformingAction(true)

    return createMessageApi({
      ...data,
      text: normalizeHtml(data.text),
    }).finally(() => {
      setPerformingAction(false)
    })
  }

  function handleSuccess({ data: successData }) {
    handleCancelReply()

    handleAddSuccessNotification('Notification.EmailRepliedTitleKey')

    return onSuccess({ data: successData }).then(() => {
      collapseMessage(data.uuid)
    })
  }

  return {
    handleCancelReply,
    handleClickForward,
    handleClickRemoveReplyDraft: handleClickRemoveDraft,
    handleClickReply,
    handleClickReplyAll,
    handleInitReplyEditor: handleInitEditor,
    handleSubmitReply: handleSubmit,
    handleSuccessReply: handleSuccess,
    replyCreationAction,
    replyFormEnabled,
    replyFormInitialValues,
    setReplyFormInitialValues,
    toggleExpandConversation,
  }
}
